/**
 * Exposure estimation for HDR / Mertens fusion.
 *
 * Two complementary paths:
 *
 *   estimateEVStops(brackets) — masked-median log-luminance per bracket;
 *     orders the stack by relative exposure and returns EV offsets in
 *     stops, normalised so the median bracket is 0. Pure pixel math —
 *     works for any input (JPEG / PNG / synthesised brackets).
 *
 *   parseExifEV(blob)         — reads ExposureTime / FNumber / ISO via
 *     piexifjs (vendored under public/vendor/piexif.min.js, exposed as
 *     globalThis.piexif). Returns the absolute exposure value or null
 *     when EXIF is missing / unparsable. Caller subtracts a reference
 *     value to get relative EVs.
 *
 * No mutation of inputs. No module-load side effects.
 */

const EPS = 1e-12;

/**
 * @typedef {object} BracketInput
 * @prop {Float32Array} rgba    width*height*4, ACEScg-linear or any linear-light buffer
 * @prop {number} width
 * @prop {number} height
 */

/**
 * Compute per-bracket EV stops by masked median log-luminance.
 *
 * Mask rules (in DISPLAY-DOMAIN proxy — quick gamma 2.2 to skip tone-map):
 *   - drop pixels whose proxy luminance < 1/255
 *   - drop pixels whose proxy luminance > 254/255
 *   - drop pixels with non-finite values (defensive)
 *
 * Why masked: shadow noise pollutes a plain log-mean, blown highlights
 * pull the median upward. Masking mid-tones gives us the "useful" range
 * of each bracket — the part that actually carries signal.
 *
 * Output: EV stops per bracket relative to the median bracket (median = 0).
 * Positive = bracket is brighter than the median. Negative = dimmer.
 *
 * @param {BracketInput[]} brackets
 * @returns {Float32Array} length = brackets.length
 */
export function estimateEVStops(brackets) {
    if (!Array.isArray(brackets) || brackets.length === 0) {
        return new Float32Array(0);
    }
    const medians = new Float64Array(brackets.length);
    for (let b = 0; b < brackets.length; b++) {
        const { rgba, width, height } = brackets[b];
        if (!rgba || !width || !height) {
            medians[b] = NaN;
            continue;
        }
        medians[b] = maskedMedianLogLum(rgba, width, height);
    }
    /* Identify the "median bracket" by sorting log-lum medians. */
    const order = Array.from(medians, (v, i) => ({ v, i }))
        .filter(e => Number.isFinite(e.v))
        .sort((a, b) => a.v - b.v);
    if (order.length === 0) {
        return new Float32Array(brackets.length); /* all NaN — all 0 EV */
    }
    const medianRef = order[Math.floor(order.length / 2)].v;
    const out = new Float32Array(brackets.length);
    for (let i = 0; i < brackets.length; i++) {
        if (!Number.isFinite(medians[i])) {
            out[i] = 0;
            continue;
        }
        /* log2 difference = EV stops directly. */
        out[i] = (medians[i] - medianRef) / Math.LN2;
    }
    return out;
}

/**
 * Masked median of natural-log luminance for a linear-light RGBA buffer.
 * Returns ln(L) median (not log2). Caller divides by LN2 to get stops.
 */
function maskedMedianLogLum(rgba, w, h) {
    const N = w * h;
    /* Pre-allocate worst-case; we'll compact at the end. */
    const buf = new Float64Array(N);
    let n = 0;
    for (let p = 0; p < N; p++) {
        const i = p * 4;
        const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
        if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) continue;
        /* Linear Rec.709 luminance — proxy is fine; ratios matter, not absolute. */
        const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        if (L <= 0) continue;
        /* Mask via quick-and-dirty display-domain proxy: gamma-1/2.2 squashed.
         * We compare against [1/255, 254/255] in display 8-bit terms. */
        const dispProxy = Math.pow(Math.min(1, Math.max(0, L)), 1 / 2.2);
        if (dispProxy < 1 / 255 || dispProxy > 254 / 255) continue;
        buf[n++] = Math.log(L + EPS);
    }
    if (n === 0) return NaN;
    /* Median via partial sort. For n up to ~50 MP, n*log(n) is fine on
     * a desktop; we run this once per bracket, not per frame. */
    const slice = buf.slice(0, n);
    slice.sort();
    const mid = n >> 1;
    return n & 1 ? slice[mid] : 0.5 * (slice[mid - 1] + slice[mid]);
}

/* ── EXIF path ───────────────────────────────────────────────────────── */

/**
 * Read a Blob as base64 dataURL ("data:<mime>;base64,..."), as expected
 * by piexifjs. Sequential — no caller-visible memory pressure.
 */
async function blobToDataURL(blob) {
    const buf = await blob.arrayBuffer();
    const u8 = new Uint8Array(buf);
    /* Build base64 in 32 KB chunks to avoid String.fromCharCode arg-limit. */
    let s = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < u8.length; i += CHUNK) {
        const end = Math.min(i + CHUNK, u8.length);
        s += String.fromCharCode.apply(null, u8.subarray(i, end));
    }
    const b64 = (typeof btoa === 'function')
        ? btoa(s)
        /* Worker-side fallback: globalThis.btoa is always present in modern
         * runtimes, but guard anyway. */
        : (globalThis.btoa ? globalThis.btoa(s) : null);
    if (b64 == null) return null;
    const mime = blob.type || 'image/jpeg';
    return `data:${mime};base64,${b64}`;
}

/**
 * Parse exposure from a JPEG Blob's EXIF, return the **absolute log2-EV**:
 *
 *   EV = log2( (FNumber^2) / ExposureTime ) - log2(ISO / 100)
 *
 * (Standard photographic definition. Higher EV = scene was brighter.)
 * Caller diffs across brackets: ev_relative_i = ev_abs_i - ev_abs_median.
 *
 * Returns null if:
 *   - piexifjs is not loaded (graceful — caller falls back to estimateEVStops)
 *   - blob is not a JPEG (no EXIF)
 *   - required tags are missing or malformed
 *
 * @param {Blob} blob
 * @returns {Promise<number | null>}
 */
export async function parseExifEV(blob) {
    if (!blob || typeof blob.arrayBuffer !== 'function') return null;
    /* piexifjs is loaded in index.html as a global; access via globalThis
     * to keep this module ESM-pure (no `window.*` references). */
    const piexif = globalThis.piexif;
    if (!piexif || typeof piexif.load !== 'function') return null;
    /* Only JPEG carries EXIF in the format piexifjs expects. */
    if (blob.type && blob.type !== 'image/jpeg' && blob.type !== 'image/jpg') return null;

    let data;
    try {
        const dataURL = await blobToDataURL(blob);
        if (!dataURL) return null;
        data = piexif.load(dataURL);
    } catch (_e) {
        return null;
    }
    if (!data || !data.Exif || !data['0th']) return null;

    const exif = data.Exif;
    const tag = piexif.ExifIFD || {};
    /* piexifjs returns rationals as [numerator, denominator] arrays. */
    const expTime = rational(exif[tag.ExposureTime]);
    const fNumber = rational(exif[tag.FNumber]);
    let iso = exif[tag.ISOSpeedRatings];
    if (Array.isArray(iso)) iso = iso[0];
    if (typeof iso !== 'number' || !Number.isFinite(iso) || iso <= 0) iso = 100;

    if (!Number.isFinite(expTime) || expTime <= 0) return null;
    if (!Number.isFinite(fNumber) || fNumber <= 0) return null;

    /* Standard EV calculation with ISO normalisation. */
    const ev = Math.log2((fNumber * fNumber) / expTime) - Math.log2(iso / 100);
    return Number.isFinite(ev) ? ev : null;
}

function rational(v) {
    if (Array.isArray(v) && v.length === 2 && v[1] !== 0) {
        return v[0] / v[1];
    }
    if (typeof v === 'number') return v;
    return NaN;
}
